import React from 'react';

import Skeleton from '~/components/core/Atomic/Skeleton';

import { LOG_PANE_DEFAULT_SIZE } from '../constants';
import useStageBar from '../StageBar/useStageBar';

import LogEntrySkeleton from './LogEntrySkeleton';

const LOG_ENTRY_COUNT = 4;
const ACTIVITY_CARD_COUNT = 3;

/**
 * The tab's shape while its sources load: the stage bar, then the log and the activity panel at
 * their opening widths, so the panes land where the placeholders were rather than jumping in. The
 * bar is left out until the conductor's stages land, then drawn as the loaded tab will draw it —
 * one placeholder per stage, and none where `StageBar` draws nothing.
 */
interface ConductorHubSkeletonProps {
  conductorStageKeys: string[] | undefined;
}

const ConductorHubSkeleton: React.FC<ConductorHubSkeletonProps> = ({ conductorStageKeys }) => {
  const steps = useStageBar(conductorStageKeys);

  return (
    <div
      role="status"
      aria-label="Loading Automation First"
      className="flex h-full w-full flex-col rounded bg-white py-16"
    >
      {steps.length ? (
        <div
          data-testid="stage-placeholders"
          className="flex items-center gap-16 border-0 border-b-1 border-solid border-slate-500 px-4 pb-12 pt-4"
        >
          {steps.map((step) => (
            <Skeleton key={step.key} variant="rectangular" height={20} className="flex-1 rounded-full" />
          ))}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-24 px-8">
        <div className="flex flex-col gap-16" style={{ width: `${LOG_PANE_DEFAULT_SIZE}%` }}>
          <Skeleton variant="text" width="30%" height={32} />
          {Array.from({ length: LOG_ENTRY_COUNT }, (_, index) => (
            <LogEntrySkeleton key={index} />
          ))}
        </div>

        <div className="flex flex-1 flex-col gap-12 px-16 pt-12">
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="rectangular" height={28} className="rounded-full" />
          <Skeleton variant="rectangular" height={28} className="rounded-full" />
          {Array.from({ length: ACTIVITY_CARD_COUNT }, (_, index) => (
            <Skeleton key={index} variant="rectangular" height={96} className="rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConductorHubSkeleton;
